import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { Field, FieldSet } from '../../components/Form';
import FormModal from '../../components/FormModal';
import LoadingIndicator from '../../components/LoadingIndicator';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import addIcon from '../../icons/non-prefilled/16x16/add.svg';
import seriesIcon from '../../icons/non-prefilled/16x16/series.svg';
import { UIActions } from '../../ui/uiSlice';
import { updateQueryList } from '../../utils/graphql/updateQueryList';
import * as styles from './AddSeriesModal.module.scss';
import AddSeriesRadioButton from './AddSeriesRadioButton';
import SeriesPostManager from './SeriesPostManager';
import SeriesSelect from './SeriesSelect';
import updatePostSeriesCache from './updatePostSeriesCache';

gql(`
  fragment AddSeriesModal_post on Post {
    id
    ...SeriesPostManager_posts
  }
`);

const ADD_SERIES_MODAL_SERIES_FRAGMENT = gql(`
  fragment AddSeriesModal_series on Series {
    id
    posts {
      id
      ...AddSeriesModal_post
    }
    ...SeriesSelect_series
  }
`);

type SeriesFragment = DocumentType<typeof ADD_SERIES_MODAL_SERIES_FRAGMENT>;

type PostFragment = SeriesFragment['posts'][number];

const ALL_SERIES_QUERY = gql(/** @complexity 28 */ `
  query allSeriesModal {
    session: currentSession {
      organization {
        id
        series {
          ...AddSeriesModal_series
        }
      }
    }
  }
`);

const CREATE_SERIES = gql(/** @complexity 26 */ `
  mutation AddSeriesModalCreateSeries($name: String!, $postIds: [ID!]!) {
    createSeries(name: $name, postIds: $postIds) {
      ...AddSeriesModal_series
    }
  }
`);

const UPDATE_SERIES = gql(/** @complexity 26 */ `
  mutation AddSeriesModalUpdateSeries(
    $id: ID!
    $name: String!
    $postIds: [ID!]!
  ) {
    updateSeries(id: $id, name: $name, postIds: $postIds) {
      ...AddSeriesModal_series
    }
  }
`);

const ADD_TO_NEW_SERIES = 'new-series';
const ADD_TO_EXISTING_SERIES = 'existing-series';

type Action = typeof ADD_TO_NEW_SERIES | typeof ADD_TO_EXISTING_SERIES;

type FormValues = {
  name: string;
  action: Action;
  posts: PostFragment[];
};

interface AddSeriesModalProps {
  close(): void;
  post: PostFragment;
}

const AddSeriesModal = ({ close, post }: AddSeriesModalProps) => {
  const dispatch = useAppDispatch();
  const [series, setSeries] = useState<SeriesFragment | null>(null);

  const [updateSeries] = useMutation(UPDATE_SERIES);
  const [createSeries] = useMutation(CREATE_SERIES, {
    update: (cache, result) => {
      updatePostSeriesCache([], 'createSeries')(cache, result);
      updateQueryList(
        { query: ALL_SERIES_QUERY },
        'session.organization.series',
        'createSeries',
        true,
      )(cache, result);
    },
  });

  const { data: { session } = {}, loading } = useQuery(ALL_SERIES_QUERY);
  const seriesList = session?.organization?.series || [];

  const onSubmit = async ({ name, posts, action }: FormValues) => {
    const postIds = posts.map(({ id }) => id);
    if (action === ADD_TO_NEW_SERIES) {
      await createSeries({ variables: { name, postIds } });
    } else {
      if (series == null) {
        throw new Error('series must not be null');
      }

      await updateSeries({
        variables: { id: series.id, name: series.name, postIds },
        update: updatePostSeriesCache(series.posts, 'updateSeries'),
      });
    }

    dispatch(UIActions.closeModal({ reset: true }));
  };

  return (
    <FormModal<FormValues>
      variant="expanded"
      title="Add to series"
      close={close}
      description={
        <>
          Add this post to a new {seriesList.length > 0 && 'or existing'} series
          - a collection of posts that are linked to each other in an order that
          you set.
        </>
      }
      form={{
        onSubmit,
        initialValues: { name: '', posts: [post], action: ADD_TO_NEW_SERIES },
        // Don't show "name is required" error
        // when switching to "Existing series"
        validateOnBlur: false,
        validateOnChange: false,
      }}
      footer={(props) => ({
        submitButton: {
          text: 'Save',
          disabled: props.values.action === ADD_TO_EXISTING_SERIES && !series,
        },
      })}
    >
      {loading && seriesList.length === 0 ? (
        <div className={styles.loadingIndicator}>
          <LoadingIndicator delay={0} />
        </div>
      ) : (
        ({ values, setFieldValue }) => (
          <>
            {(seriesList.length > 0 ||
              values.action === ADD_TO_EXISTING_SERIES) && (
              <div className={styles.radioButtonGroup}>
                <AddSeriesRadioButton
                  name="action"
                  value={ADD_TO_NEW_SERIES}
                  title="New series"
                  icon={<SVGIcon>{addIcon}</SVGIcon>}
                  onChange={() => setFieldValue('posts', [post])}
                />
                <AddSeriesRadioButton
                  name="action"
                  value={ADD_TO_EXISTING_SERIES}
                  title="Existing series"
                  icon={<SVGIcon>{seriesIcon}</SVGIcon>}
                  onChange={() =>
                    setFieldValue(
                      'posts',
                      (series ? series.posts : []).concat(post),
                    )
                  }
                />
              </div>
            )}
            {values.action === ADD_TO_NEW_SERIES ? (
              <Field
                autoFocus
                label="Name"
                name="name"
                placeholder="Choose a name for your new series"
                required
                type="text"
              />
            ) : (
              <FieldSet legend="Series">
                <SeriesSelect
                  series={series}
                  setSeries={(newSeries) => {
                    setSeries(newSeries);
                    setFieldValue('posts', newSeries.posts.concat(post));
                  }}
                  options={seriesList}
                />
              </FieldSet>
            )}
            {(values.action !== ADD_TO_EXISTING_SERIES || series) && (
              <SeriesPostManager
                posts={values.posts}
                requiredPostId={post.id}
                seriesId={
                  values.action === ADD_TO_EXISTING_SERIES && series
                    ? series?.id
                    : undefined
                }
                onChange={(value) => setFieldValue('posts', value)}
              />
            )}
          </>
        )
      )}
    </FormModal>
  );
};

export default AddSeriesModal;
